/*
 * Field reference for the API documentation. This is the public contract of
 * POST /v1/precheck and POST /v1/precheck-pos: the required flags and allowed
 * values mirror the backend DTOs (create-precheck.dto.ts,
 * precheck-groups.dto.ts, create-precheck-pos.dto.ts) — keep them in sync.
 *
 * Descriptions and group titles are translated: apiDocs.fields.<key> and
 * apiDocs.groups.<id> in the locale files.
 */

export type Requirement = 'yes' | 'no' | 'matchingService' | { service: string };

export interface DocField {
  name: string;
  // translation key below apiDocs.fields, where it differs from the name
  key?: string;
  type: string;
  required: Requirement;
  values?: string[];
}

export interface DocFieldGroup {
  // apiDocs.groups.<id>; for service groups also the JSON property
  id: string;
  fields: DocField[];
}

const YES_NO = ['yes', 'no'];
const YES: Requirement = 'yes';
const NO: Requirement = 'no';
const requiredFor = (service: string): Requirement => ({ service });

// Mirrors SERVICES_BY_REGION in the backend's create-precheck.dto.ts
export const SERVICES_BY_REGION: Record<string, string[]> = {
  us: ['creditCard', 'affiliate', 'clearingHouse', 'alternativeBilling', 'corporateBanking', 'whitelabelGateway'],
  eu: ['creditCard', 'affiliate', 'alternativeBilling', 'corporateBanking', 'whitelabelGateway'],
  other: ['creditCard', 'affiliate', 'whitelabelGateway'],
};

const SERVICES = Array.from(new Set(Object.values(SERVICES_BY_REGION).flat()));

const API_OPTIONS: DocField[] = [
  { name: 'notifyMerchant', type: 'boolean', required: NO },
  { name: 'externalReference', type: 'string (max. 255)', required: NO },
  { name: 'test', type: 'boolean', required: NO },
];

const CC = requiredFor('creditCard');
const AP = requiredFor('affiliate');
const WL = requiredFor('whitelabelGateway');
const CH = requiredFor('clearingHouse');
const CB = requiredFor('corporateBanking');
const AB = requiredFor('alternativeBilling');

export const ECOM_FIELDS: DocFieldGroup[] = [
  {
    id: 'root',
    fields: [
      { name: 'reg', type: 'string', required: YES, values: Object.keys(SERVICES_BY_REGION) },
      { name: 'srv', type: 'string[]', required: YES, values: SERVICES },
      { name: 'genInfo', type: 'object', required: YES },
      {
        name: 'ccpInfo, apInfo, wlInfo, chInfo, cbInfo, abInfo',
        key: 'serviceGroups',
        type: 'object',
        required: 'matchingService',
      },
      ...API_OPTIONS,
    ],
  },
  {
    id: 'genInfo',
    fields: [
      { name: 'genComp', type: 'string', required: YES },
      { name: 'genAddr', type: 'string', required: YES },
      { name: 'genDirName', type: 'string', required: YES },
      { name: 'genDirEmail', type: 'string', required: YES },
      { name: 'genDirAddr', type: 'string', required: YES },
      { name: 'genTaxID', type: 'string', required: YES },
      { name: 'genDesc', type: 'string', required: YES },
      { name: 'genPrice', type: 'string', required: YES },
      { name: 'genUrl', type: 'string', required: YES },
      { name: 'genUser', type: 'string', required: NO },
      { name: 'genPass', type: 'string', required: NO },
      { name: 'genFullName', type: 'string', required: YES },
      { name: 'genEmail', type: 'string (email)', required: YES },
      { name: 'genSkype', type: 'string', required: NO },
      { name: 'genPhone', type: 'string', required: NO },
      { name: 'genRefSource', type: 'string', required: NO },
    ],
  },
  {
    id: 'ccpInfo',
    fields: [
      { name: 'ccpFulfill', type: 'string', required: CC, values: YES_NO },
      { name: 'ccpCallCtr', type: 'string', required: CC, values: YES_NO },
      { name: 'ccpLic', type: 'string', required: CC, values: YES_NO },
      { name: 'ccpLicJur', type: 'string', required: NO },
      { name: 'ccpBankAcq', type: 'string', required: CC },
      { name: 'ccpCurrMCC', type: 'string', required: NO },
      { name: 'ccpAvgTxVal', type: 'string', required: CC },
      { name: 'ccpCurrMonthVol', type: 'string', required: CC },
      { name: 'ccpExpMonthVol', type: 'string', required: CC },
      { name: 'ccpDescLine1', type: 'string', required: NO },
      { name: 'ccpDescLine2', type: 'string', required: NO },
      { name: 'ccpPrefGateway', type: 'string', required: NO },
      { name: 'ccpProcCurr', type: 'string', required: NO },
      { name: 'ccpSettleCurr', type: 'string', required: NO },
      { name: 'ccpTargetMarkets', type: 'string', required: NO },
    ],
  },
  {
    id: 'apInfo',
    fields: [
      { name: 'apRegNum', type: 'string', required: AP },
      { name: 'apUsage', type: 'string', required: AP },
      { name: 'apProjVolume', type: 'string', required: AP },
      { name: 'apProjCount', type: 'string', required: AP },
      { name: 'apAvgTxValue', type: 'string', required: AP },
      { name: 'apCardCount', type: 'string', required: AP },
      { name: 'apCurrencies', type: 'string', required: AP },
      {
        name: 'apProduct',
        type: 'string[]',
        required: AP,
        values: ['Bank Transfers', 'Payouts', 'Physical Pre-Paid Cards', 'Virtual Pre-Paid Cards'],
      },
    ],
  },
  {
    id: 'wlInfo',
    fields: [
      { name: 'wlExpMonthVol', type: 'string', required: WL },
      { name: 'wlExpTxCount', type: 'string', required: WL },
      { name: 'wlAvgTxVal', type: 'string', required: WL },
      { name: 'wlLicense', type: 'string', required: WL, values: YES_NO },
      { name: 'wlLicJur', type: 'string', required: NO },
      {
        name: 'wlGatewayFeatures',
        type: 'string[]',
        required: WL,
        values: [
          'Risk management',
          'Load balancing',
          'Chargeback & fraud alert management',
          'MID configuration',
          'BIN & IP block',
          'Other',
        ],
      },
      { name: 'wlOtherGateway', type: 'string', required: NO },
      { name: 'wlTargetMarkets', type: 'string', required: WL },
      { name: 'wlBankAcquirers', type: 'string', required: WL },
      { name: 'wlAltBilling', type: 'string', required: WL },
      { name: 'wlOtherRemarks', type: 'string', required: NO },
    ],
  },
  {
    id: 'chInfo',
    fields: [
      { name: 'chPayee', type: 'string', required: CH },
      { name: 'chClient', type: 'string', required: CH },
      { name: 'chCustEmail', type: 'string', required: CH },
      { name: 'chMktDesc', type: 'string', required: CH },
      { name: 'chCompHandling', type: 'string', required: CH },
      { name: 'chRefundPol', type: 'string', required: CH, values: YES_NO },
      { name: 'chAchProvider', type: 'string', required: CH },
      { name: 'chAchName', type: 'string', required: CH },
      { name: 'chAchPhone', type: 'string', required: CH },
      { name: 'chAchEmail', type: 'string', required: CH },
      { name: 'chHighTxAmt', type: 'string', required: CH },
      { name: 'chAvgTxAmt', type: 'string', required: CH },
      { name: 'chMaxTxPerDay', type: 'string', required: CH },
      { name: 'chReqMonthLimit', type: 'string', required: CH },
      { name: 'chRecTxPerc', type: 'string', required: CH },
      { name: 'chRefundCap', type: 'string', required: CH, values: YES_NO },
      { name: 'chAchCreditCap', type: 'string', required: CH, values: YES_NO },
    ],
  },
  {
    id: 'cbInfo',
    fields: [
      { name: 'cbBusinessAddr', type: 'string', required: NO },
      { name: 'cbPEP', type: 'string', required: CB },
      { name: 'cbMainClients', type: 'string', required: CB },
      { name: 'cbFromCountry', type: 'string', required: CB },
      { name: 'cbCurrency', type: 'string', required: CB },
      { name: 'cbMonthlyVol', type: 'string', required: CB },
      { name: 'cbNumPayments', type: 'string', required: CB },
      { name: 'cbIncomingFunds', type: 'string', required: CB },
      { name: 'cbToCountry', type: 'string', required: CB },
      { name: 'cbOutgoingFunds', type: 'string', required: CB },
    ],
  },
  {
    id: 'abInfo',
    fields: [
      { name: 'abProduct', type: 'string[]', required: AB, values: ['sepa', 'sofort'] },
      { name: 'abProjectedVolume', type: 'string', required: AB },
      { name: 'abProjectedTxCount', type: 'string', required: AB },
      { name: 'abLowestTxAmount', type: 'string', required: AB },
      { name: 'abHighestTxAmount', type: 'string', required: AB },
      { name: 'abAvgTxAmount', type: 'string', required: AB },
      { name: 'abTargetMarkets', type: 'string', required: AB },
      { name: 'abSalesTurnover', type: 'string', required: AB },
    ],
  },
];

export const POS_FIELDS: DocFieldGroup[] = [
  {
    id: 'body',
    fields: [
      { name: 'company', type: 'string', required: YES },
      { name: 'taxId', type: 'string', required: YES },
      { name: 'creditorId', type: 'string', required: NO },
      { name: 'industry', type: 'string', required: NO },
      { name: 'address', type: 'string', required: YES },
      { name: 'locations', type: 'string', required: NO },
      { name: 'website', type: 'string (URL)', required: NO },
      { name: 'director', type: 'string', required: YES },
      { name: 'contactPerson', type: 'string', required: YES },
      { name: 'email', type: 'string (email)', required: YES },
      { name: 'telephone', type: 'string', required: YES },
      { name: 'mobile', type: 'string', required: NO },
      { name: 'onlineShop', type: 'string (URL)', required: NO },
      { name: 'currentProvider', type: 'string', required: YES },
      { name: 'turnover', type: 'string', required: YES },
      { name: 'receiptSize', type: 'string', required: YES },
      { name: 'bankStatementReference', type: 'string', required: YES },
      { name: 'terminals', type: 'string', required: YES, values: YES_NO },
      { name: 'checkoutConnection', type: 'string', required: YES, values: YES_NO },
      { name: 'cashCut', type: 'string', required: YES, values: ['standard', 'detailed'] },
      { name: 'tip', type: 'string', required: YES, values: ['no', 'signature', 'without-signature'] },
      {
        name: 'cards',
        type: 'string[]',
        required: YES,
        values: ['Visa/Mastercard', 'Discover/Diners', 'American Express', 'VPAY/Maestro', 'Girocard/EC cash', 'China Union Pay'],
      },
      {
        name: 'additionalServices',
        type: 'string[]',
        required: NO,
        values: ['Cash pooling', 'Online document archiving', 'DCC', 'PayPerLink'],
      },
      ...API_OPTIONS,
    ],
  },
];
